import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CAPABILITIES } from "@/lib/data";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-bone">
      {/* HERO */}
      <section className="pt-20 md:pt-28 pb-20 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-vermilion mb-8">Capabilities</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Everything needed
              <br />
              to ship a product company.
              <br />
              <span className="italic text-ink/60">Under one roof.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border">
            <p className="text-lg text-ink/70 leading-relaxed">
              Six integrated disciplines, led by senior practitioners, staffed for
              depth. No off-shore handoffs, no project-manager firewalls — only
              builders accountable to the product.
            </p>
          </div>
        </div>
      </section>

      {/* ACCORDION */}
      <section className="py-20 md:py-28">
        <div className="tr-container">
          <Accordion type="single" collapsible defaultValue="applied-ai" className="border-t border-border">
            {CAPABILITIES.map((c) => (
              <AccordionItem
                key={c.id}
                value={c.id}
                className="border-b border-border"
                data-testid={`capability-${c.id}`}
              >
                <AccordionTrigger className="py-8 md:py-10 hover:no-underline group [&>svg]:hidden">
                  <div className="grid grid-cols-12 gap-6 w-full text-left items-baseline">
                    <span className="font-mono text-xs text-vermilion col-span-2 md:col-span-1">
                      {c.number}
                    </span>
                    <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight col-span-8 md:col-span-8 leading-none">
                      {c.title}
                    </h3>
                    <div className="col-span-2 md:col-span-3 flex justify-end">
                      <Plus className="w-5 h-5 md:w-6 md:h-6 transition-transform duration-500 group-data-[state=open]:rotate-45" />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-12 gap-6 pb-10 md:pb-14">
                    <div className="col-span-12 md:col-span-8 md:col-start-2">
                      <p className="text-lg md:text-xl text-ink/70 leading-relaxed max-w-3xl">
                        {c.blurb}
                      </p>
                      <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                        {c.bullets.map((b) => (
                          <li
                            key={b}
                            className="flex items-start gap-3 text-sm text-ink border-t border-border pt-3"
                          >
                            <span className="font-mono text-[11px] text-vermilion pt-[2px]">
                              →
                            </span>
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32 bg-ink text-white">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-10 mb-16">
            <div className="col-span-12 md:col-span-5">
              <div className="overline text-vermilion mb-6">How we deliver</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.02] tracking-tight">
                Methodical in pace.
                <br />
                <span className="italic text-white/60">Editorial in craft.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-0 border-t border-white/15">
            {[
              {
                n: "01",
                title: "Discovery",
                body: "A week of founder-level conversations, architecture review, and a written problem statement everyone signs.",
              },
              {
                n: "02",
                title: "Embed",
                body: "A senior pod lands — architect, engineers, designer, PM — with clear charters and a 90-day outcome contract.",
              },
              {
                n: "03",
                title: "Ship",
                body: "We release in tight, reviewable increments. Evals, dashboards and ADRs are first-class artifacts.",
              },
              {
                n: "04",
                title: "Operate",
                body: "On-call, runbooks, cost governance and a research cadence so the product keeps compounding.",
              },
            ].map((s, i) => (
              <Reveal
                key={s.n}
                delay={i}
                className={`col-span-12 md:col-span-6 lg:col-span-3 p-8 md:p-10 border-b border-white/15 ${
                  i !== 3 ? "md:border-r border-white/15" : ""
                }`}
              >
                <div className="font-mono text-xs text-vermilion mb-4">{s.n}</div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">{s.body}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-16">
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="inline-flex items-center gap-2 border border-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-ink transition-colors"
            >
              Brief our team <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
